import React from "react";
import {
  CreditCard, FileText, ShieldCheck, DollarSign,
  Zap, AlertTriangle,
} from "lucide-react";
import RichBlogLayout from "../../components/RichBlogLayout";
import {
  Prose, PullQuote, MetricsRow, BeforeAfter, ProcessFlow,
  ParallelGrid, FeatureCallout, AgentCard,
  LiveFeed, ChecklistCard, SectionDivider,
} from "../../components/blog/BlogGraphics";

const BlogAliciaPaymentPostingUnderpayments = () => (
  <RichBlogLayout
    category="Revenue Cycle Management"
    eyebrow="Payment Posting"
    title="Alicia: Payment Posting at 10× Speed — and the Underpayments You'd Never Catch Manually"
    subtitle="ERAs and paper EOBs, posted, reconciled, and audited against your contracted fee schedule on every line — at 99.9% accuracy."
    author="Asha Team"
    date="May 2, 2026"
    readTime="8 min read"
  >
    <Prose>
      <p>
        Open the EOB stack on a Wednesday afternoon and the math gets ugly fast.
        Twenty minutes per claim is the conservative number — open the document,
        read the line items, post the payment, adjust the patient balance, fix
        the contractual write-off, move on. Multiply that by a queue that never
        gets shorter and the practice ends up with a biller whose entire
        afternoon is consumed by a workflow that, on paper, sounds simple.
      </p>
      <p>
        And the truly expensive thing isn't the speed. It's what gets missed at
        speed. A crown downcoded to a buildup. A fee schedule that hasn't been
        renegotiated since 2021 quietly accepting a payment that's $34 short.
        An alternate benefit silently applied with no callout. None of those
        feel like errors when you're moving fast — they feel like normal,
        because they post and the screen turns green.
      </p>
    </Prose>

    <AgentCard
      name="Alicia"
      role="AI Payment Posting · ERA + EOB Reconciliation"
      initial="A"
      description="Alicia automates payment posting end-to-end — electronic ERAs and scanned paper EOBs — at roughly 10× the speed of a human poster, with 99.9% accuracy. She doesn't just post what the payer sent. She verifies every line against your contracted fee schedule and your ledger before anything closes."
      channels={["835 ERA", "Paper EOB OCR", "PMS ledger sync", "Fee-schedule audit"]}
    />

    <Prose>
      <h2>Posting isn't the expensive part. The leakage is.</h2>
      <p>
        A typical multi-doctor practice receives 80–150 EOBs per week across
        carriers. At 20 minutes per claim that is days of biller time —
        recurring, every week, forever. Most practices accept that as a fixed
        cost of being in business. They shouldn't.
      </p>
      <p>
        The reason it stays a fixed cost is that the work is genuinely tedious
        and easy to defer. Post the obvious ones. Stack the weird ones. Promise
        yourself you'll come back to the queue on Friday. By the time Friday
        arrives, the underpaid claim from Tuesday is buried under three new
        days of EOBs and the $34 is gone forever.
      </p>
    </Prose>

    <MetricsRow
      items={[
        { value: 10, suffix: "×", label: "Faster than a human poster" },
        { value: 99.9, decimals: 1, suffix: "%", label: "Posting accuracy" },
        { value: 20, prefix: "~", suffix: " min", label: "Saved per claim" },
        { value: 100, suffix: "%", label: "Claims fee-schedule audited" },
      ]}
    />

    <Prose>
      <p>
        Alicia removes the deferral. Every EOB and every ERA is read, posted,
        reconciled, and audited the moment it lands. There is no Friday queue
        because there is no queue.
      </p>

      <h2>Posting is the easy part. Verification is the unlock.</h2>
      <p>
        Most practices that have looked at posting automation have looked at
        speed. Speed is real and it matters, but speed alone solves the wrong
        problem. The expensive problem is the underpayment that never gets
        flagged. A 10× faster poster that still rubber-stamps a downcode is just
        a faster way to lose money.
      </p>
    </Prose>

    <BeforeAfter
      beforeTitle="The traditional posting workflow"
      afterTitle="With Alicia"
      beforeItems={[
        "Biller opens each EOB or ERA, reads line by line, posts manually",
        "Roughly 20 minutes per claim; the queue grows faster than it shrinks",
        "Underpayments and downcodes slip through unless someone audits later",
        "Paper EOBs sit in a stack waiting for a slow afternoon",
      ]}
      afterItems={[
        "Both ERA and scanned paper EOB are read and posted automatically",
        "Each line cross-checked against the contracted fee schedule, every time",
        "Discrepancies — downcoded, alt-benefited, mis-allocated — flagged immediately",
        "Ledger and patient responsibility split written cleanly into the PMS",
      ]}
    />

    <Prose>
      <h2>Every check Alicia runs on a single posted line</h2>
      <p>
        Alicia is not running a single check. She runs six checks against the
        same line of an ERA at once, and only marks the line "clean" when
        every one of them passes. That concurrency is the difference between
        automation and verification.
      </p>
    </Prose>

    <ProcessFlow
      steps={[
        {
          title: "Reads the document — ERA or scanned paper",
          body:
            "Electronic 835 files are parsed natively. Paper EOBs are OCR'd, structured into the same canonical line items, and treated identically downstream.",
        },
        {
          title: "Matches every line to the original claim",
          body:
            "Each procedure on the EOB is paired against the submitted claim using procedure code, tooth number, surface, and date of service.",
        },
        {
          title: "Validates the allowed amount against your fee schedule",
          body:
            "Alicia loads the contracted fee schedule for that carrier and plan and checks that the payer's allowed amount matches. A $34 short-pay isn't lost — it's flagged.",
        },
        {
          title: "Detects downcodes and alternate benefits",
          body:
            "When a payer pays out at a lower-cost procedure than what was billed, Alicia surfaces the substitution explicitly with the original code, the substituted code, and the dollar delta.",
        },
        {
          title: "Posts cleanly and splits patient responsibility",
          body:
            "Insurance payment, contractual write-off, and patient responsibility are written directly into the ledger with the correct split — and patient balance moves into the next workflow.",
        },
      ]}
    />

    <PullQuote>
      Speed isn't the most important part. What really matters is what she catches.
    </PullQuote>

    <Prose>
      <h2>One line, six checks, all at once</h2>
      <p>
        Take a real example: a D2740 — porcelain crown — submitted last Tuesday
        for Maria Lopez. The carrier's ERA arrives at 8:42 AM today. A human
        poster would key in the payer's allowed amount and move on. Alicia
        runs the line through every check at once.
      </p>
    </Prose>

    <ChecklistCard
      title="Pre-post audit · D2740 crown · Maria Lopez"
      items={[
        { label: "Procedure code matched to original claim line", status: "pass" },
        { label: "Tooth #14, distal-occlusal-mesial — surfaces match", status: "pass" },
        { label: "Allowed amount matches contracted fee schedule ($1,184)", status: "pass" },
        { label: "Insurance paid at expected 50% co-insurance", status: "pass" },
        { label: "No downcode or alternate benefit applied", status: "pass" },
        { label: "Patient responsibility split posted cleanly to ledger", status: "pass" },
      ]}
    />

    <Prose>
      <p>
        Every one of those checks took place in roughly the time it takes a
        human poster to find the document on the shared drive. And the moment
        any one of them failed — say the allowed amount came in at $1,150
        instead of $1,184 — that line wouldn't post silently. It would land in
        the exception queue with the dollar delta, the contracted rate, and
        the carrier's stated rate side by side.
      </p>

      <h2>An hour of posting, compressed</h2>
      <p>
        On a normal Tuesday morning, your posting queue between 8 AM and 9 AM
        looks like this — except the work is already done by the time you sit
        down with coffee.
      </p>
    </Prose>

    <LiveFeed
      title="Posting activity · Tuesday morning"
      items={[
        { time: "08:14", label: "Posted ERA · Delta Dental", detail: "$1,840.00 · 4 claims · all verified", status: "done" },
        { time: "08:22", label: "Posted ERA · Aetna", detail: "$612.50 · 2 claims · 1 flagged underpayment", status: "done" },
        { time: "08:31", label: "Posted paper EOB · MetLife", detail: "$348.00 · scanned + OCR · clean", status: "done" },
        { time: "08:42", label: "Posted ERA · Cigna", detail: "$2,114.10 · 6 claims · D2740 downcode flagged", status: "done" },
        { time: "08:55", label: "Posted ERA · Guardian", detail: "$928.00 · 3 claims · all verified", status: "done" },
        { time: "09:03", label: "Posted ERA · UnitedHealthcare", detail: "$1,287.40 · 5 claims · all verified", status: "done" },
      ]}
    />

    <Prose>
      <h2>The exception queue is the actual product</h2>
      <p>
        When 99.9% of posting happens automatically, the work that lands on a
        biller's desk changes character entirely. It is no longer "post the easy
        stuff and hope the hard stuff resolves itself." It is a focused queue
        of claims where Alicia is not fully confident — exceptions, with
        context attached, ready for human judgment.
      </p>
    </Prose>

    <ParallelGrid
      title="What Alicia is doing across the practice — at the same time"
      columns={3}
      items={[
        { title: "ERA · Delta Dental", subtitle: "12 claims · auto-posting", icon: FileText, status: "active" },
        { title: "Paper EOB · Cigna", subtitle: "OCR in progress", icon: FileText, status: "active" },
        { title: "Fee-schedule audit", subtitle: "PPO contracted rates", icon: ShieldCheck, status: "active" },
        { title: "Underpayment flag", subtitle: "Aetna · D2950 · -$58", icon: AlertTriangle, status: "active" },
        { title: "Posted to ledger", subtitle: "MetLife · 4 claims", icon: CreditCard, status: "done" },
        { title: "Patient balance updated", subtitle: "Routed to Rachel", icon: DollarSign, status: "done" },
      ]}
    />

    <FeatureCallout
      stat="10×"
      label="Faster than a human poster — and verified at every line"
      description="A task that took your biller all afternoon now finishes before they sit down, with every payment audited against the contracted fee schedule before it touches the ledger."
      icon={Zap}
    />

    <Prose>
      <h2>What's recovered for the team</h2>
      <p>
        The biller who used to spend afternoons posting now spends them
        recovering money. The contracted-rate variance that used to be
        invisible is now a routed, contextual task. The paper-EOB stack that
        used to slip a week is processed the day it arrives. And the patient
        balance moves to the next step — Rachel's tone-matched outreach — the
        same day insurance posts, not three weeks later.
      </p>
      <p>
        From the practice owner's seat, the most important shift is the
        smallest: the underpayments you used to never see become a number you
        can manage. A monthly dashboard of recovered short-pays is not a thing
        most practices have. With Alicia, it becomes the default.
      </p>
    </Prose>

    <SectionDivider label="Bottom line" />

    <Prose>
      <p>
        Posting is the part of revenue cycle that looks solved and isn't. Alicia
        does the rote work in seconds and uses the time she saves to verify
        every line against your real contracted rates. The result is faster
        posting, cleaner ledgers, and recovered revenue you would never have
        known to look for.
      </p>
    </Prose>
  </RichBlogLayout>
);

export default BlogAliciaPaymentPostingUnderpayments;
